import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Mail, Phone, MapPin, Download, FileText } from "lucide-react";
import DownloadBrochureButton from "@/components/ledger/DownloadBrochureButton";
import { useSeo } from "@/hooks/useSeo";
import Navbar from "@/components/ledger/Navbar";
import ScrollProgress from "@/components/ledger/ScrollProgress";
import InViewReveal from "@/components/ledger/InViewReveal";
import SectionWatermark from "@/components/ledger/SectionWatermark";
import FloatingWhatsApp from "@/components/velora/FloatingWhatsApp";

/**
 * Catalogue page - full editorial spice book.
 *
 * Inspired structurally by the Alric / Viceroy Impex 16-page brochure
 * (cover → about → vision/mission → core values → why spices → spice
 * intro → 4 hero feature spreads → guide grids → closing) but rebuilt
 * inside the Vilora ledger aesthetic (warm cream + ink + brass + vermilion).
 *
 * Psychology levers applied (cited in code):
 *   • Warm tones (red / orange / yellow) stimulate appetite + emotional
 *     positivity in food contexts (Spence, Crossmodal correspondences)
 *   • Hero photography full-bleed at the start of each spread - Schwarz
 *     processing-fluency: vivid imagery raises perceived quality
 *   • Whitespace + minimal copy on luxury pages → premium positioning
 *     (Reinecke/Tuch low VC + high PT)
 *   • Storytelling + named origin → emotional connection → conversion
 *   • Visual hierarchy via display serif headlines + mono kickers + drop
 *     caps + brass hairlines, never boxes - keeps it editorial, not SaaS
 */

const HERO_SPICES = [
  {
    id: "cumin",
    folio: "I",
    name: "Cumin",
    binomial: "Cuminum cyminum",
    origin: "Gujarat · Rajasthan",
    grade: "Singapore (99.5%) · Europe (99%)",
    image: "/catalogue/cumin.jpg",
    body:
      "From the seeds of Cuminum cyminum - earthy, warm, faintly bitter. A staple across Mexican, Indian, African and Asian cuisines, cumin carries the kind of weight that opens a curry and grounds a tagine. We export sortex-cleaned single-origin cumin in 25/50 kg PP and food-grade jute, with COA on every lot.",
    aura: "linear-gradient(180deg, #C9A26B 0%, #7E5023 100%)",
  },
  {
    id: "turmeric",
    folio: "II",
    name: "Turmeric",
    binomial: "Curcuma longa",
    origin: "Andhra Pradesh · Tamil Nadu · Maharashtra",
    grade: "Curcumin 3% / 5% / 7% +",
    image: "/catalogue/turmeric.jpg",
    body:
      "The brilliant yellow rhizome ground from underground stems of Curcuma longa. Our Erode + Salem lots are graded by curcumin content; export grades range from culinary 3% through nutraceutical 7%+. Steam-sterilised on request, cold-ground to retain colour and oil.",
    aura: "linear-gradient(180deg, #E8A219 0%, #8A4F08 100%)",
  },
  {
    id: "red-chilli",
    folio: "III",
    name: "Red Chilli",
    binomial: "Capsicum annuum",
    origin: "Guntur · Byadgi · Kashmir",
    grade: "ASTA 90 to 220 colour units",
    image: "/catalogue/red-chilli.jpg",
    body:
      "From the slow-burn warmth of Byadgi to the colour-rich pungency of Guntur Sannam, we move stem-cut and stemless chilli for retail packing, ground masala, and oleoresin extraction. Sortex-cleaned, capsaicin and ASTA tested per lot, fumigated to buyer requirement.",
    aura: "linear-gradient(180deg, #D9352A 0%, #6E1408 100%)",
  },
  {
    id: "fennel",
    folio: "IV",
    name: "Fennel",
    binomial: "Foeniculum vulgare",
    origin: "Gujarat · Rajasthan",
    grade: "Europe Special · Singapore · Standard",
    image: "/catalogue/fennel.jpg",
    body:
      "Pale-green oval seeds with a clean, sweet, licorice-edged aroma. Packed for the long haul in vacuum-sealed liners or breathable jute. Used whole, crushed, or ground - equally at home in a fish broth, a paan supari, or a cold-press tea.",
    aura: "linear-gradient(180deg, #B7C97A 0%, #5C6B30 100%)",
  },
];

const PLATE_SPICES = [
  {
    id: "jaggery",
    title: "Jaggery",
    subtitle: "Unrefined sweetness",
    image: "/catalogue/jaggery.jpg",
    body:
      "Boiled from sugarcane juice or palm sap until it crystallises. A traditional sweetener with a richer, more complex flavour than refined sugar - and a richer trace-mineral profile too. Available in solid block, granular, and powder forms; organic and conventional.",
    folio: "PLATE V",
  },
  {
    id: "cinnamon",
    title: "Cinnamon",
    subtitle: "Bark of Cinnamomum",
    image: "/catalogue/cinnamon.jpg",
    body:
      "From the inner bark of Cinnamomum trees - warm, sweet, faintly woody. Ceylon (true cinnamon) and Cassia available in quill, broken, and powder. A small amount of cinnamaldehyde does most of the work; we test for it and certify on the manifest.",
    folio: "PLATE VI",
  },
  {
    id: "star-anise",
    title: "Star Anise",
    subtitle: "Fruit of Illicium verum",
    image: "/catalogue/star-anise.jpg",
    body:
      "Eight-pointed pericarps with a licorice-warm aroma - central to Chinese five-spice, Vietnamese pho broths, and the Indian garam masala family. We supply autumn-flowered grade for traders distilling shikimic acid and culinary grade for retail and HoReCa.",
    folio: "PLATE VII",
  },
];

const SPICE_GUIDE = [
  {
    name: "Fenugreek",
    binomial: "Trigonella foenum-graecum",
    blurb:
      "Maple-syrup nose, slightly bitter. Seeds whole or ground; leaves fresh or dried (kasuri methi). A workhorse of curries, dals, pickles.",
  },
  {
    name: "Black Pepper",
    binomial: "Piper nigrum",
    blurb:
      "Tellicherry, Malabar Garbled, MG-1. Pungent, aromatic, depth-giving. Sortex grade, oil content tested, packed for moisture stability.",
  },
  {
    name: "Curry Leaf",
    binomial: "Murraya koenigii",
    blurb:
      "Aromatic leaves of the curry tree - South Indian staple. Available fresh (cold-chain) and dried; powder available on contract.",
  },
  {
    name: "Bay Leaf",
    binomial: "Laurus nobilis · Cinnamomum tamala",
    blurb:
      "Indian (tamala) and Mediterranean (Laurus) varieties. Whole dried leaves, hand-sorted, packed flat to retain shape.",
  },
];

const SPICE_BENEFIT = [
  {
    name: "Coriander",
    binomial: "Coriandrum sativum",
    blurb:
      "Round, ridged seeds - gentle, citrusy, faintly soapy. Whole or split; ground to spec. Eagle / parrot grade.",
  },
  {
    name: "Mustard",
    binomial: "Brassica spp.",
    blurb:
      "Yellow, brown, and black mustard. Whole seed, split, or oil. Used in tempering, pickling, and cold-pressed oil production.",
  },
  {
    name: "Sesame",
    binomial: "Sesamum indicum",
    blurb:
      "Hulled and natural; white, black, mixed. High-protein, oil-rich. Sortex-cleaned for confectionary and tahini stockists.",
  },
  {
    name: "Clove",
    binomial: "Syzygium aromaticum",
    blurb:
      "Aromatic flower buds - strong, warm, peppery from eugenol. Headed and headless grades. Origin: Madagascar / Indonesia / India.",
  },
];

/* ─── RICE — 2026 brochure additions ─────────────────────────────────── */
const BROCHURE_PDF = "/vilora-impex-brochure.pdf";

const RICE_PRODUCTS = [
  {
    id: "basmati-1121-steam",
    folio: "I",
    name: "Basmati Rice 1121 Steam",
    grade: "Long grain 8.30 mm+ · steam-processed · aged 12 months minimum",
    origin: "Punjab · Haryana",
    image: "/catalogue/rice-basmati-1121-steam.jpg",
    body:
      "Offers extra-long, fluffy grains and a rich aroma perfect for any gourmet dish. Carefully steamed to ensure superior elongation and texture — the ideal choice for authentic biryanis and pilafs.",
  },
  {
    id: "basmati-1509-steam",
    folio: "II",
    name: "Basmati Rice 1509 Steam",
    grade: "Long grain 8.0–8.4 mm · short-duration premium · HoReCa grade",
    origin: "Punjab · Haryana",
    image: "/catalogue/rice-basmati-1509-steam.jpg",
    body:
      "Features slender, non-sticky grains that expand beautifully for perfect biryanis. This premium variety delivers a delicate flavour and fluffy texture, elevating every meal with authentic quality.",
  },
  {
    id: "basmati-1718-steam",
    folio: "III",
    name: "Basmati Rice 1718 Steam",
    grade: "Long grain · disease-resistant 1121 successor · steam-processed",
    origin: "Punjab · Haryana",
    image: "/hero-rice.jpg",
    body:
      "Offers extraordinary aroma and slender, fluffy grains perfect for traditional biryanis. Known for its natural elongation and non-sticky texture, it brings authentic quality and elegance to every meal.",
  },
  {
    id: "sona-masoori",
    folio: "IV",
    name: "Sona Masoori Rice",
    grade: "Medium grain · low starch · everyday-rice premium",
    origin: "Andhra Pradesh · Telangana · Karnataka",
    image: "/catalogue/rice-sona-masoori.jpg",
    body:
      "Lightweight, aromatic, and soft texture — perfect for everyday South Indian meals. Renowned for easy digestibility and a non-sticky finish; the ideal choice for authentic pongal, upma, and daily rice dishes.",
  },
];

const FLAVOR_WORLD = [
  {
    name: "Nutmeg",
    binomial: "Myristica fragrans",
    blurb:
      "Whole nuts (with or without shell) and powder. Mace separately. Used in baking, savoury sauces, fragrance.",
  },
  {
    name: "Oregano",
    binomial: "Origanum vulgare",
    blurb:
      "Dried leaves whole or rubbed. Mediterranean and Indian varieties. Antioxidant-rich. Foodservice + retail packed.",
  },
  {
    name: "Cardamom",
    binomial: "Elettaria · Amomum",
    blurb:
      "Green (small) and black (large). Whole pods, seeds, or ground. Premium 8mm bold sortex grade for the Gulf trade.",
  },
];

const VALUES = [
  {
    n: "i",
    title: "Quality",
    body: "Lab-tested and FSSAI / APEDA aligned. Every lot, every shipment, on file.",
  },
  {
    n: "ii",
    title: "Customer Care",
    body: "A dedicated buyer desk in your timezone. Same-day reply window, weekly status notes.",
  },
  {
    n: "iii",
    title: "Innovation",
    body: "Cold-grinding to retain colour and oil. Modified atmosphere packing for long-haul shipment.",
  },
  {
    n: "iv",
    title: "Sustainability",
    body: "Direct-from-farmer programmes. Lot-level traceability. Recyclable jute and BOPP.",
  },
];

const REASONS = [
  { num: "01", title: "Flavour & Aroma", body: "The single largest reason - spices change a dish from edible to memorable." },
  { num: "02", title: "Preservation", body: "Antimicrobial compounds (curcumin, eugenol, cinnamaldehyde) extend shelf life - a use as old as the trade itself." },
  { num: "03", title: "Health Benefits", body: "Antioxidants, anti-inflammatory compounds, digestive aids. The ledger of nutrition is long." },
  { num: "04", title: "Cultural Significance", body: "Every cuisine has a spice fingerprint. To trade in spice is to trade in culture." },
];

export default function CataloguePage() {
  useSeo({
    title: "Spice & Export Catalogue | Vilora Impex — Chilli, Turmeric, Cumin, Coriander, Basmati",
    description:
      "Vilora Impex export catalogue: single-origin chilli, turmeric, cumin, coriander, fennel, basmati & dry fruit. FSSAI & APEDA registered, shipped from Gujarat.",
    path: "/catalogue",
    image: "https://www.viloraimpex.com/og.jpg",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "CollectionPage",
          "@id": "https://www.viloraimpex.com/catalogue#collection",
          "name": "Vilora Impex Spice & Export Catalogue",
          "description":
            "The Vilora Impex export catalogue — single-origin Indian spices, premium long-grain rice, dry fruit, and pulses for international buyers.",
          "url": "https://www.viloraimpex.com/catalogue",
          "inLanguage": "en",
          "isPartOf": { "@id": "https://www.viloraimpex.com/#website" },
          "about": { "@id": "https://www.viloraimpex.com/#organization" },
          "mainEntity": {
            "@type": "ItemList",
            "name": "Featured spices",
            "itemListOrder": "https://schema.org/ItemListOrderAscending",
            "numberOfItems": 15,
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "item": { "@type": "Product", "name": "Cumin (Jeera)", "category": "Spice", "brand": { "@id": "https://www.viloraimpex.com/#organization" }, "description": "Single-origin Indian cumin seeds, oil-content tested, steam-sterilized." } },
              { "@type": "ListItem", "position": 2, "item": { "@type": "Product", "name": "Turmeric (Haldi)", "category": "Spice", "brand": { "@id": "https://www.viloraimpex.com/#organization" }, "description": "High-curcumin turmeric, whole and powder, exported from Gujarat." } },
              { "@type": "ListItem", "position": 3, "item": { "@type": "Product", "name": "Red Chilli", "category": "Spice", "brand": { "@id": "https://www.viloraimpex.com/#organization" }, "description": "Whole and ground red chilli, colour-graded and ASTA-tested." } },
              { "@type": "ListItem", "position": 4, "item": { "@type": "Product", "name": "Fennel (Saunf)", "category": "Spice", "brand": { "@id": "https://www.viloraimpex.com/#organization" }, "description": "Premium fennel seeds, graded for aroma retention." } },
              { "@type": "ListItem", "position": 5, "item": { "@type": "Product", "name": "Fenugreek (Methi)", "category": "Spice", "brand": { "@id": "https://www.viloraimpex.com/#organization" } } },
              { "@type": "ListItem", "position": 6, "item": { "@type": "Product", "name": "Black Pepper", "category": "Spice", "brand": { "@id": "https://www.viloraimpex.com/#organization" } } },
              { "@type": "ListItem", "position": 7, "item": { "@type": "Product", "name": "Curry Leaf", "category": "Spice", "brand": { "@id": "https://www.viloraimpex.com/#organization" } } },
              { "@type": "ListItem", "position": 8, "item": { "@type": "Product", "name": "Bay Leaf (Tej Patta)", "category": "Spice", "brand": { "@id": "https://www.viloraimpex.com/#organization" } } },
              { "@type": "ListItem", "position": 9, "item": { "@type": "Product", "name": "Coriander (Dhania)", "category": "Spice", "brand": { "@id": "https://www.viloraimpex.com/#organization" } } },
              { "@type": "ListItem", "position": 10, "item": { "@type": "Product", "name": "Mustard (Sarson)", "category": "Spice", "brand": { "@id": "https://www.viloraimpex.com/#organization" } } },
              { "@type": "ListItem", "position": 11, "item": { "@type": "Product", "name": "Sesame (Til)", "category": "Spice", "brand": { "@id": "https://www.viloraimpex.com/#organization" } } },
              { "@type": "ListItem", "position": 12, "item": { "@type": "Product", "name": "Clove (Laung)", "category": "Spice", "brand": { "@id": "https://www.viloraimpex.com/#organization" } } },
              { "@type": "ListItem", "position": 13, "item": { "@type": "Product", "name": "Nutmeg (Jaiphal)", "category": "Spice", "brand": { "@id": "https://www.viloraimpex.com/#organization" } } },
              { "@type": "ListItem", "position": 14, "item": { "@type": "Product", "name": "Oregano", "category": "Spice", "brand": { "@id": "https://www.viloraimpex.com/#organization" } } },
              { "@type": "ListItem", "position": 15, "item": { "@type": "Product", "name": "Cardamom (Elaichi)", "category": "Spice", "brand": { "@id": "https://www.viloraimpex.com/#organization" } } }
            ]
          }
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.viloraimpex.com/" },
            { "@type": "ListItem", "position": 2, "name": "Catalogue", "item": "https://www.viloraimpex.com/catalogue" }
          ]
        }
      ]
    },
  });

  return (
    <main className="ledger catalogue-page relative min-h-screen">
      <ScrollProgress />
      <Navbar />

      <CatalogueCover />
      <CatalogueBrochureCTA />
      <CatalogueAbout />
      <CatalogueValues />
      <CatalogueReasons />
      <CatalogueRice />
      <CatalogueSpiceIntro />
      {HERO_SPICES.map((s, i) => (
        <CatalogueHeroSpice key={s.id} spice={s} reversed={i % 2 === 1} />
      ))}
      <CataloguePlate spice={PLATE_SPICES[0]} />
      <CatalogueGuideGrid
        kicker="THE SPICE GUIDE · I"
        title="Backbone aromatics."
        subtitle="The four most-asked-after across our daily ledger."
        items={SPICE_GUIDE}
      />
      <CataloguePlate spice={PLATE_SPICES[1]} />
      <CatalogueGuideGrid
        kicker="THE SPICE GUIDE · II"
        title="The benefits desk."
        subtitle="Workhorse seeds and buds - the volume movers of the trade."
        items={SPICE_BENEFIT}
      />
      <CataloguePlate spice={PLATE_SPICES[2]} />
      <CatalogueGuideGrid
        kicker="THE SPICE GUIDE · III"
        title="A world of flavour."
        subtitle="Specialty and finishing spices - small lots, high margin."
        items={FLAVOR_WORLD}
      />
      <CatalogueClose />

      <FloatingWhatsApp />
    </main>
  );
}

/* ─── COVER ─────────────────────────────────────────────────────────── */

function CatalogueCover() {
  return (
    <section className="cat-cover relative" aria-label="Catalogue cover">
      <div className="cat-cover-art" aria-hidden="true">
        <img
          src="/hero-spices.jpg"
          alt=""
          loading="eager"
          decoding="async"
        />
        <div className="cat-cover-shade" />
      </div>

      <div className="cat-cover-shell">
        <div className="cat-cover-folio">
          <Link to="/" className="cat-back-link">
            <ArrowLeft size={14} strokeWidth={1.6} />
            <span>Back to Home</span>
          </Link>
          <span className="rule rule-grow" />
          <span className="folio">CATALOGUE · 2025</span>
        </div>

        <InViewReveal className="cat-cover-stack" delay={1}>
          <span className="cat-cover-eyebrow mono">VILORA IMPEX · GUJARAT</span>
          <h1 className="cat-cover-title display">
            The
            <br />
            <em className="display-italic vermilion">Spice Book.</em>
          </h1>
          <p className="cat-cover-deck">
            Eighteen spices, four origins, one house - graded by hand, weighed
            twice, sealed at port. <em>Of grain, of spice, of trust.</em>
          </p>

          <div className="cat-cover-meta">
            <span>Plate I - XVIII</span>
            <span className="cat-cover-dot" aria-hidden="true">◆</span>
            <span>Edition 2025</span>
            <span className="cat-cover-dot" aria-hidden="true">◆</span>
            <span>Mundra Port · India</span>
          </div>
        </InViewReveal>
      </div>
    </section>
  );
}

/* ─── ABOUT ─────────────────────────────────────────────────────────── */

function CatalogueAbout() {
  return (
    <section className="cat-about relative paper" style={{ overflow: "hidden" }}>
      <SectionWatermark variant="monogram" position="top-right" size={420} opacity={0.14} />

      <div className="cat-shell">
        <InViewReveal className="cat-folio">
          <span className="folio">№ 01</span>
          <span className="rule rule-grow" style={{ flex: 1, height: 1 }} />
          <span className="folio" style={{ color: "var(--ink-mute)" }}>
            ABOUT THE HOUSE
          </span>
        </InViewReveal>

        <InViewReveal className="cat-about-spread" delay={1}>
          <h2 className="cat-section-title display">
            We are <em className="display-italic vermilion">a one-stop desk</em>
            <br />
            for premium Indian spice.
          </h2>
          <p className="cat-paragraph cat-dropcap">
            Vilora Impex is the export desk of a Gujarat trading house with
            fifteen years of counter-and-warehouse discipline behind it. We
            source from the same farms the family has bought from for years,
            grade by hand, weigh twice, photograph every container, and stamp
            our own seal on the bill of lading. <em>Storing, handling,
            processing, packing, transport - under one ledger.</em>
          </p>
          <p className="cat-paragraph">
            Fresh-quality spice needs constant management and on-time delivery.
            Our team procures from farm-gate, sortex-cleans, lab-tests at
            origin, and ships through Mundra and Kandla to ports in the Gulf,
            Africa, Europe, the Americas, and Asia Pacific.
          </p>
        </InViewReveal>
      </div>
    </section>
  );
}

/* ─── VISION / MISSION / VALUES ───────────────────────────────────── */

function CatalogueValues() {
  return (
    <section className="cat-values relative paper" style={{ overflow: "hidden" }}>
      <div className="cat-shell">
        <InViewReveal className="cat-folio">
          <span className="folio">№ 02</span>
          <span className="rule rule-grow" style={{ flex: 1, height: 1 }} />
          <span className="folio" style={{ color: "var(--ink-mute)" }}>
            VISION · MISSION · VALUES
          </span>
        </InViewReveal>

        <div className="cat-vm-row">
          <InViewReveal className="cat-vm-col" delay={1}>
            <span className="cat-vm-kicker mono">- Vision -</span>
            <p className="cat-vm-statement">
              <em className="display-italic">
                To be the trusted Indian origin desk for serious global buyers
                of grain, spice, and dry goods.
              </em>
            </p>
          </InViewReveal>
          <InViewReveal className="cat-vm-col" delay={2}>
            <span className="cat-vm-kicker mono">- Mission -</span>
            <p className="cat-vm-statement">
              <em className="display-italic">
                To remove uncertainty from food export - origin, grade, paper,
                port - visible inside one accountable file.
              </em>
            </p>
          </InViewReveal>
        </div>

        <InViewReveal className="cat-values-grid" delay={3}>
          {VALUES.map((v) => (
            <article key={v.title} className="cat-value">
              <span className="cat-value-num display-italic">{v.n}.</span>
              <h3 className="cat-value-title">{v.title}</h3>
              <p className="cat-value-body">{v.body}</p>
            </article>
          ))}
        </InViewReveal>
      </div>
    </section>
  );
}

/* ─── REASONS ───────────────────────────────────────────────────────── */

function CatalogueReasons() {
  return (
    <section className="cat-reasons relative" style={{ overflow: "hidden" }}>
      <div className="cat-shell">
        <InViewReveal className="cat-folio">
          <span className="folio">№ 03</span>
          <span className="rule rule-grow" style={{ flex: 1, height: 1 }} />
          <span className="folio" style={{ color: "var(--ink-mute)" }}>
            WHY USE SPICES
          </span>
        </InViewReveal>

        <InViewReveal className="cat-reasons-head" delay={1}>
          <h2 className="cat-section-title display">
            Four reasons, repeated
            <br />
            <em className="display-italic vermilion">across cuisines.</em>
          </h2>
        </InViewReveal>

        <div className="cat-reasons-grid">
          {REASONS.map((r, i) => (
            <InViewReveal as="article" delay={i + 1} className="cat-reason" key={r.num}>
              <span className="cat-reason-num mono">{r.num}</span>
              <h3 className="cat-reason-title">{r.title}</h3>
              <p className="cat-reason-body">{r.body}</p>
            </InViewReveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── SPICE INTRO ───────────────────────────────────────────────────── */

function CatalogueSpiceIntro() {
  return (
    <section className="cat-intro relative paper" style={{ overflow: "hidden" }}>
      <SectionWatermark variant="compass" position="top-left" size={380} opacity={0.13} />

      <div className="cat-shell">
        <InViewReveal className="cat-folio">
          <span className="folio">№ 04</span>
          <span className="rule rule-grow" style={{ flex: 1, height: 1 }} />
          <span className="folio" style={{ color: "var(--ink-mute)" }}>
            ABOUT SPICE
          </span>
        </InViewReveal>

        <div className="cat-intro-spread">
          <InViewReveal className="cat-intro-left" delay={1}>
            <h2 className="cat-section-title display">
              Dried parts of plants -
              <br />
              <em className="display-italic vermilion">seeds, fruits, bark.</em>
            </h2>
            <p className="cat-paragraph">
              Spices are the dried parts of plants used to flavour, preserve,
              and colour food: <em>seeds</em> (cumin, fennel), <em>fruits</em>
              {" "}(chilli, allspice), <em>flowers</em> (clove, saffron),
              {" "}<em>bark</em> (cinnamon), <em>roots</em> (ginger, turmeric),
              even <em>leaves</em> (bay, curry leaf).
            </p>
            <p className="cat-paragraph">
              We move all of the above. The next pages are our working list -
              the spices that move out of Mundra most weeks of the year.
            </p>
          </InViewReveal>
          <InViewReveal className="cat-intro-chips" delay={2}>
            <span className="cat-chip-label mono">- FROM THE LEDGER -</span>
            <ul>
              <li>Cumin</li>
              <li>Turmeric</li>
              <li>Red Chilli</li>
              <li>Fennel</li>
              <li>Jaggery</li>
              <li>Cinnamon</li>
              <li>Fenugreek</li>
              <li>Black Pepper</li>
              <li>Curry Leaf</li>
              <li>Bay Leaf</li>
              <li>Coriander</li>
              <li>Mustard</li>
              <li>Sesame</li>
              <li>Clove</li>
              <li>Star Anise</li>
              <li>Nutmeg</li>
              <li>Oregano</li>
              <li>Cardamom</li>
            </ul>
          </InViewReveal>
        </div>
      </div>
    </section>
  );
}

/* ─── HERO SPICE ────────────────────────────────────────────────────── */

function CatalogueHeroSpice({ spice, reversed }) {
  return (
    <section
      className={`cat-hero-spice relative ${reversed ? "is-reversed" : ""}`}
      style={{ overflow: "hidden", "--spice-aura": spice.aura }}
    >
      <div className="cat-hero-aura" aria-hidden="true" />

      <div className="cat-shell">
        <div className="cat-hero-grid">
          <div className="cat-hero-media">
            <div className="cat-hero-frame">
              <img src={spice.image} alt={spice.name} loading="lazy" />
              <span className="cat-hero-folio mono">PLATE · {spice.folio}</span>
              <div className="cat-hero-corner cat-hero-corner--tl" aria-hidden="true" />
              <div className="cat-hero-corner cat-hero-corner--br" aria-hidden="true" />
            </div>
          </div>

          <div className="cat-hero-copy">
            <InViewReveal>
              <span className="cat-hero-binomial mono">{spice.binomial}</span>
              <h2 className="cat-hero-name display">{spice.name}</h2>
              <p className="cat-hero-body">{spice.body}</p>
            </InViewReveal>

            <InViewReveal delay={2}>
              <dl className="cat-hero-spec">
                <div>
                  <dt>Origin</dt>
                  <dd>{spice.origin}</dd>
                </div>
                <div>
                  <dt>Grade</dt>
                  <dd>{spice.grade}</dd>
                </div>
              </dl>
            </InViewReveal>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── PLATE (full-bleed single-product spread) ────────────────────── */

function CataloguePlate({ spice }) {
  return (
    <section className="cat-plate relative" aria-label={spice.title}>
      <div className="cat-plate-art">
        <img src={spice.image} alt={spice.title} loading="lazy" />
        <div className="cat-plate-shade" />
      </div>
      <div className="cat-plate-overlay">
        <span className="cat-plate-folio mono">{spice.folio}</span>
        <h2 className="cat-plate-title display">
          {spice.title}
        </h2>
        <span className="cat-plate-sub display-italic">{spice.subtitle}</span>
        <p className="cat-plate-body">{spice.body}</p>
      </div>
    </section>
  );
}

/* ─── GUIDE GRID (3-up + 1) ───────────────────────────────────────── */

function CatalogueGuideGrid({ kicker, title, subtitle, items }) {
  return (
    <section className="cat-guide relative paper" style={{ overflow: "hidden" }}>
      <div className="cat-shell">
        <InViewReveal className="cat-folio">
          <span className="folio">{kicker}</span>
          <span className="rule rule-grow" style={{ flex: 1, height: 1 }} />
        </InViewReveal>

        <InViewReveal className="cat-guide-head" delay={1}>
          <h2 className="cat-section-title display">{title}</h2>
          <p className="cat-guide-sub">{subtitle}</p>
        </InViewReveal>

        <div className="cat-guide-grid">
          {items.map((it, i) => (
            <InViewReveal as="article" delay={i + 1} className="cat-guide-item" key={it.name}>
              <span className="cat-guide-num mono">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="cat-guide-name">{it.name}</h3>
              <span className="cat-guide-binomial">{it.binomial}</span>
              <p className="cat-guide-blurb">{it.blurb}</p>
            </InViewReveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── CLOSE ────────────────────────────────────────────────────────── */

function CatalogueClose() {
  return (
    <section className="cat-close relative paper" style={{ overflow: "hidden" }}>
      <SectionWatermark variant="compass" position="center" size={520} opacity={0.07} />

      <InViewReveal className="cat-close-inner">
        <div className="cat-folio">
          <span className="folio">№ XVIII · END</span>
          <span className="rule rule-grow" style={{ flex: 1, height: 1 }} />
          <span className="folio" style={{ color: "var(--ink-mute)" }}>
            EXPORT DESK
          </span>
        </div>

        <h2 className="cat-section-title display">
          One desk,
          <br />
          <em className="display-italic vermilion">all eighteen spices.</em>
        </h2>
        <p className="cat-paragraph">
          Tell us the commodity, the destination port, the date you would like
          the cargo to arrive. We will write back the same day with a price, a
          sample plan, and the documents we will need from you.
        </p>

        <div className="cat-close-contact">
          <a href="mailto:exports@viloraimpex.com" className="cat-close-link">
            <Mail size={14} strokeWidth={1.7} />
            <span>exports@viloraimpex.com</span>
          </a>
          <a href="tel:+918669999901" className="cat-close-link">
            <Phone size={14} strokeWidth={1.7} />
            <span>+91 86699 99901</span>
          </a>
          <span className="cat-close-link" style={{ borderColor: "transparent" }}>
            <MapPin size={14} strokeWidth={1.7} />
            <span>Rajkot, Gujarat - India</span>
          </span>
        </div>

        <div className="cat-close-actions">
          <Link to="/#inquiry" className="btn-primary">
            <span className="btn-code">№ 06</span>
            <span>Open a Conversation</span>
            <span className="btn-arrow">
              <ArrowRight size={14} strokeWidth={1.7} />
            </span>
          </Link>
          <DownloadBrochureButton variant="ghost" />
          <Link to="/" className="btn-ghost">
            <span>Back to Home</span>
            <span className="btn-arrow">→</span>
          </Link>
        </div>

        <span className="cat-close-finis display-italic">
          - finis -
        </span>
      </InViewReveal>
    </section>
  );
}

/* ─── BROCHURE DOWNLOAD CTA ──────────────────────────────────────────── */

function CatalogueBrochureCTA() {
  return (
    <>
      <CatalogueRiceStyles />
      <section className="cat-brochure paper" aria-label="Download the export brochure">
        <div className="cat-brochure-shell">
          <div className="cat-brochure-icon" aria-hidden="true">
            <FileText size={28} strokeWidth={1.4} />
          </div>
          <div className="cat-brochure-text">
            <h2 className="display cat-brochure-title">The Vilora Impex Brochure.</h2>
            <p className="cat-brochure-deck">
              Premium rice, pulses &amp; spices &mdash; the full product spread, our supply
              process, and the company contact card. Share with your buying desk.
            </p>
          </div>
          <DownloadBrochureButton variant="primary" />
        </div>
      </section>
    </>
  );
}

/* ─── RICE PLATE ─────────────────────────────────────────────────────── */

function CatalogueRice() {
  return (
    <section className="cat-rice relative" aria-labelledby="cat-rice-h2">
      <div className="cat-rice-shell">
        <InViewReveal as="div" className="cat-rice-head">
          <span className="folio cat-rice-eyebrow">PLATE &middot; RICE</span>
          <h2 id="cat-rice-h2" className="display cat-rice-title">Rice.</h2>
          <p className="cat-rice-deck">
            Carefully selected varieties with uniform grain structure and controlled
            moisture levels. Direct from milling partners in Punjab, Haryana, Andhra
            Pradesh and Telangana. Sortex-cleaned in two passes, aged to spec, packed
            5&ndash;50 kg in PP, BOPP, jute or buyer-branded private label.
          </p>
          <div className="cat-rice-cta-row">
            <DownloadBrochureButton variant="inline" label="Full rice spread in the PDF brochure" />
          </div>
        </InViewReveal>

        <div className="cat-rice-grid">
          {RICE_PRODUCTS.map((r) => (
            <InViewReveal key={r.id} as="article" className="cat-rice-card">
              <figure className="cat-rice-thumb">
                <img src={r.image} alt={`${r.name} — Vilora Impex`} loading="lazy" width="640" height="480" />
              </figure>
              <div className="cat-rice-meta">
                <span className="folio cat-rice-folio">PLATE {r.folio}</span>
                <h3 className="display cat-rice-name">{r.name}</h3>
                <p className="cat-rice-grade mono">{r.grade}</p>
                <p className="cat-rice-origin mono">Origin: {r.origin}</p>
                <p className="cat-rice-body">{r.body}</p>
              </div>
            </InViewReveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CatalogueRiceStyles() {
  return (
    <style>{`
      .cat-brochure {
        padding: clamp(28px, 4vw, 48px) clamp(20px, 4vw, 40px);
        background:
          linear-gradient(120deg, rgba(216, 177, 91, 0.16) 0%, rgba(168, 132, 61, 0.08) 50%, rgba(244, 239, 227, 0.04) 100%),
          var(--paper-1, #FAF6EA);
        border-top: 1px solid rgba(168, 132, 61, 0.34);
        border-bottom: 1px solid rgba(168, 132, 61, 0.34);
      }
      .cat-brochure-shell {
        max-width: 1080px; margin: 0 auto;
        display: grid;
        grid-template-columns: auto 1fr auto;
        gap: clamp(16px, 3vw, 32px);
        align-items: center;
      }
      .cat-brochure-icon {
        width: 56px; height: 56px;
        display: inline-flex; align-items: center; justify-content: center;
        color: var(--brass-deep, #846530);
        background: rgba(216, 177, 91, 0.18);
        border: 1px solid rgba(216, 177, 91, 0.55);
      }
      .cat-brochure-folio {
        display: block;
        font-size: 10.5px;
        letter-spacing: 0.22em;
        color: var(--brass-deep, #846530);
        margin-bottom: 6px;
      }
      .cat-brochure-title {
        font-size: clamp(24px, 3vw, 34px);
        line-height: 1.1;
        margin: 0 0 6px;
        color: var(--ink);
      }
      .cat-brochure-deck {
        font-family: 'Crimson Pro', Georgia, serif;
        font-size: clamp(15px, 1.4vw, 17px);
        line-height: 1.5;
        color: var(--ink-soft);
        margin: 0;
        max-width: 560px;
      }
      .cat-brochure-btn {
        display: inline-flex;
        align-items: center;
        gap: 14px;
        padding: 14px 22px;
        background: var(--ink, #1A1F36);
        color: var(--bone, #F4EFE3);
        border: 1.5px solid var(--brass, #A8843D);
        text-decoration: none;
        font-family: 'JetBrains Mono', monospace;
        font-weight: 600;
        text-transform: uppercase;
        box-shadow: 4px 4px 0 rgba(168, 132, 61, 0.42);
        transition: transform 220ms ease, box-shadow 220ms ease, background 220ms ease, border-color 220ms ease;
        white-space: nowrap;
      }
      .cat-brochure-btn:hover, .cat-brochure-btn:focus-visible {
        background: var(--brass-deep, #846530);
        border-color: var(--bone, #F4EFE3);
        transform: translate(-2px, -2px);
        box-shadow: 6px 6px 0 var(--brass, #A8843D);
        outline: none;
      }
      .cat-brochure-btn-code {
        font-size: 10px; letter-spacing: 0.22em;
        color: var(--brass, #A8843D);
      }
      .cat-brochure-btn-label { font-size: 13px; letter-spacing: 0.10em; }
      .cat-brochure-btn-arrow {
        display: inline-flex; align-items: center;
        color: var(--brass, #A8843D);
        transition: transform 220ms ease;
      }
      .cat-brochure-btn:hover .cat-brochure-btn-arrow { transform: translateY(2px); }

      @media (max-width: 768px) {
        .cat-brochure-shell { grid-template-columns: 1fr; text-align: left; }
        .cat-brochure-icon { width: 44px; height: 44px; }
        .cat-brochure-btn { justify-content: center; }
      }

      /* RICE PLATE */
      .cat-rice {
        padding: clamp(56px, 7vw, 96px) clamp(20px, 4vw, 40px);
        background:
          radial-gradient(ellipse 100% 60% at 50% 0%, rgba(168, 132, 61, 0.08), transparent 70%),
          var(--paper-1, #FAF6EA);
        position: relative;
      }
      .cat-rice::before {
        content: '';
        position: absolute;
        top: 0; left: clamp(24px, 4vw, 60px); right: clamp(24px, 4vw, 60px);
        height: 1px;
        background: linear-gradient(90deg, transparent, var(--brass, #A8843D) 30%, var(--brass, #A8843D) 70%, transparent);
        opacity: 0.6;
      }
      .cat-rice-shell { max-width: 1180px; margin: 0 auto; }
      .cat-rice-head { text-align: center; max-width: 760px; margin: 0 auto clamp(40px, 5vw, 60px); }
      .cat-rice-eyebrow {
        display: inline-block;
        font-size: 11px; letter-spacing: 0.24em;
        color: var(--brass-deep, #846530);
        margin-bottom: 14px;
      }
      .cat-rice-title {
        font-size: clamp(48px, 7vw, 88px);
        line-height: 0.98;
        margin: 0 0 18px;
        color: var(--ink);
        letter-spacing: -0.01em;
      }
      .cat-rice-deck {
        font-family: 'Crimson Pro', Georgia, serif;
        font-size: clamp(16px, 1.5vw, 18px);
        line-height: 1.62;
        color: var(--ink-soft);
        margin: 0 0 16px;
      }
      .cat-rice-cta-row { display: inline-flex; }
      .cat-rice-pdf-link {
        display: inline-flex; align-items: center; gap: 8px;
        font-family: 'JetBrains Mono', monospace;
        font-size: 11.5px; letter-spacing: 0.14em; text-transform: uppercase;
        color: var(--brass-deep, #846530);
        text-decoration: none;
        border-bottom: 1px dotted rgba(168, 132, 61, 0.55);
        padding-bottom: 2px;
        transition: color 200ms ease, border-color 200ms ease;
      }
      .cat-rice-pdf-link:hover { color: var(--vermilion, #C8431F); border-bottom-color: var(--vermilion, #C8431F); }

      .cat-rice-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        gap: clamp(24px, 3vw, 40px);
      }
      .cat-rice-card {
        display: flex;
        flex-direction: column;
        background: var(--bone, #F4EFE3);
        border: 1px solid rgba(168, 132, 61, 0.28);
        overflow: hidden;
        box-shadow: 0 18px 36px -22px rgba(60, 44, 18, 0.30);
        transition: transform 300ms ease, box-shadow 300ms ease, border-color 300ms ease;
      }
      .cat-rice-card:hover {
        transform: translateY(-4px);
        border-color: var(--brass, #A8843D);
        box-shadow: 0 28px 48px -24px rgba(60, 44, 18, 0.40);
      }
      .cat-rice-thumb {
        margin: 0;
        aspect-ratio: 4 / 3;
        background: var(--paper-2, #ECE3CE);
        overflow: hidden;
        border-bottom: 1px solid rgba(168, 132, 61, 0.20);
      }
      .cat-rice-thumb img {
        width: 100%; height: 100%;
        object-fit: cover;
        display: block;
        transition: transform 600ms ease;
      }
      .cat-rice-card:hover .cat-rice-thumb img { transform: scale(1.05); }
      .cat-rice-meta { padding: 22px 22px 26px; }
      .cat-rice-folio {
        display: block; font-size: 10px; letter-spacing: 0.22em;
        color: var(--brass-deep, #846530);
        margin-bottom: 8px;
      }
      .cat-rice-name {
        font-size: clamp(22px, 2.4vw, 28px);
        line-height: 1.15;
        margin: 0 0 10px;
        color: var(--ink);
        letter-spacing: -0.002em;
      }
      .cat-rice-grade, .cat-rice-origin {
        font-size: 10.5px;
        letter-spacing: 0.10em;
        color: var(--ink-mute);
        margin: 0 0 4px;
        text-transform: uppercase;
      }
      .cat-rice-origin { margin-bottom: 14px; color: var(--brass-deep, #846530); }
      .cat-rice-body {
        font-family: 'Crimson Pro', Georgia, serif;
        font-size: 15.5px;
        line-height: 1.6;
        color: var(--ink-soft);
        margin: 0;
      }
    `}</style>
  );
}
